import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReplacementsPayload } from "@/services/workbench_api";
import { flavorFormulaKeys } from "./useFlavorFormulas";
import monitoring from "@/lib/monitoring";
import { registerAutoFormulateIngredients } from "@/utils/serverCompounds";
import { serverURL } from "@/utils/serverURL";

// Pull every candidate substance out of a "ready" payload so we can prime
// the client compound registry. Without this, applying a replacement that
// adds a candidate the user has never seen before drops the row from the
// flask UI until a page refresh re-fetches /formulas (which DOES register
// items). Covers singles, multis, and extraItems (foods that participate
// in candidate sets).
function candidatesFromPayload(
  payload: Extract<ReplacementsPayload, { status: "ready" }>,
): Array<{
  ndbTable: "public.compound" | "public.food";
  rowid: number;
  name?: string | null;
  cas?: string | null;
}> {
  const out: Array<{
    ndbTable: "public.compound" | "public.food";
    rowid: number;
    name?: string | null;
    cas?: string | null;
  }> = [];
  const normalizeTable = (t: string): "public.compound" | "public.food" | null =>
    t === "public.compound" || t === "public.food" ? t : null;

  for (const perItem of Object.values(payload.perItem)) {
    for (const set of [...perItem.singles, ...perItem.multis]) {
      for (const item of set.items) {
        const tbl = normalizeTable(item.itemNdbTable);
        if (!tbl) continue;
        out.push({
          ndbTable: tbl,
          rowid: item.itemRowid,
          name: item.name,
          cas: item.cas,
        });
      }
    }
  }
  for (const extra of payload.extraItems) {
    const tbl = normalizeTable(extra.itemNdbTable);
    if (!tbl) continue;
    out.push({ ndbTable: tbl, rowid: extra.itemRowid });
  }
  return out;
}

type Params = {
  flavorId: string | null;   // UUID
  formulaId: number | null;
  versionId: number | null;
};

// Single channel — the server's /events SSE stream emits the full payload
// both on connect and on every state transition. There is no separate
// /replacements REST endpoint anymore.
export function useWorkbenchReplacements({ flavorId, formulaId, versionId }: Params) {
  const queryClient = useQueryClient();

  // SSE subscription: every "replacements_state" event carries the FULL
  // payload (status + perItem + extraItems). We write it straight into the
  // React Query cache so consumers keep their existing `useQuery` shape
  // and structural-sharing semantics — no separate useState mirror needed.
  //
  // When the payload's status transitions to "ready" we ALSO invalidate
  // `flavorFormulaKeys.byFlavor` so WorkbenchPage's
  // `dropDeltaByCompoundId` map can pick up fresh `formulas[].items`
  // (needed to translate ffviIds in the perItem keys back to compoundIds).
  // No autosave-time invalidate; the refetch fires only when fresh data
  // is actually about to be consumed.
  useEffect(() => {
    if (!flavorId || !formulaId || !versionId) return;
    const url = serverURL`/me/flavor/${flavorId}/formulas/${formulaId}/versions/${versionId}/events`;
    const es = new EventSource(url, { withCredentials: true });
    let lastStatus: ReplacementsPayload["status"] | null = null;

    const handler = (e: MessageEvent) => {
      let payload: ReplacementsPayload;
      try {
        payload = JSON.parse(e.data) as ReplacementsPayload;
      } catch (err) {
        monitoring.warn("replacements_invalid_payload", err instanceof Error ? err : new Error(String(err)));
        return;
      }
      queryClient.setQueryData(["replacements", versionId], payload);
      if (payload.status === "ready") {
        // Prime the compound registry with every candidate so onApplyPreview's
        // swap into the formulation lands on a row FlaskPanel/FlaskRow can
        // actually render. Idempotent — registerAutoFormulateIngredients
        // patches placeholders + skips writes when the entry is already
        // populated.
        registerAutoFormulateIngredients(candidatesFromPayload(payload));
        if (lastStatus !== "ready") {
          queryClient.invalidateQueries({
            queryKey: flavorFormulaKeys.byFlavor(flavorId),
          });
        }
      }
      lastStatus = payload.status;
    };
    es.addEventListener("replacements_state", handler as EventListener);

    return () => {
      es.removeEventListener("replacements_state", handler as EventListener);
      es.close();
    };
  }, [flavorId, formulaId, versionId, queryClient]);

  // React Query becomes a passive cache reader here — the queryFn is never
  // called because we always have data (written by the SSE listener) or
  // pending (also written by the SSE listener). The queryFn placeholder is
  // a safety net for an unlikely race where the consumer mounts before the
  // SSE listener has had a chance to deliver the first state event.
  return useQuery<ReplacementsPayload>({
    queryKey: ["replacements", versionId],
    queryFn: () =>
      Promise.resolve({ status: "pending" } satisfies ReplacementsPayload),
    enabled: flavorId != null && formulaId != null && versionId != null,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
